"use client";

import { Loader } from "@nimble/react-requests/components/shared/loader";
import { Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { jobViewActionsTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";

interface QuickApplyConfirmationModalViewProps {
  isOpen: boolean;
  isPending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function QuickApplyConfirmationModalView({
  isOpen,
  isPending,
  error,
  onConfirm,
  onClose,
}: QuickApplyConfirmationModalViewProps) {
  const confirmIcon = isPending ? (
    <Loader className="mr-2 h-4 w-4" />
  ) : (
    <Zap className="mr-2 h-4 w-4" />
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="
          sm:max-w-[640px]
          p-6
          border-2 border-primary/50
          ring-2 ring-primary/20
          rounded-xl
        "
      >
        <DialogHeader className="text-left space-y-3">
          <div className="flex items-start gap-3">
            <DialogTitle className="text-xl sm:text-2xl font-bold leading-tight">
              Good news! This school is part of the Quick Apply network.
            </DialogTitle>
          </div>
          <div className="rounded-lg bg-muted/60 p-4">
            <DialogDescription className="text-base text-foreground/80">
              <p className="font-semibold text-foreground/90">
                You can use your Nimble profile to apply in just a few clicks.
              </p>
              <ul className="mt-3 list-disc space-y-2 pl-5">
                <li>
                  Use your Nimble profile to easily apply to any schools with
                  the Quick Apply feature.
                </li>
                <li>Apply at least 2x faster to this district&apos;s roles.</li>
              </ul>
            </DialogDescription>
          </div>
          <p className="mt-1 px-4">
            <span className="font-semibold text-foreground/90">
              What to expect:
            </span>{" "}
            Quick Apply will open the district&apos;s application page in a new
            tab. If you already have a Nimble account, log in with the same
            email. If not, you&apos;ll create one — it only takes a minute.
          </p>
        </DialogHeader>

        <div className="mt-5">
          <Button
            onClick={onConfirm}
            disabled={isPending}
            className="h-12 w-full rounded-xl bg-gradient-to-r from-accent-purple to-info hover:from-accent-purple-hover hover:to-info-hover text-base font-semibold text-primary-foreground hover:opacity-90"
          >
            {confirmIcon}
            &quot;Quick Apply&quot;
          </Button>
          {error && (
            <p
              className="mt-2 text-sm text-destructive"
              role="alert"
              data-testid={
                jobViewActionsTestIds.JOB_VIEW_ACTIONS_QUICK_APPLY_ERROR
              }
            >
              {error}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
